/**
 * PricingSection.jsx — seção de planos da homepage (vitrine pública)
 * ==================================================================
 * Modelo híbrido (decisão 07/07/2026): grade como verdade + extras à la carte.
 *
 *   - 4 cards (Basic / Basic+ / Pro / Elite) + toggle S/M/L/XL que troca a
 *     célula exibida. Preço do card = preço REAL da célula (não "a partir de").
 *   - "Personalize sua equipe" (opcional, expandível): steppers de extras
 *     somando por cima da célula.
 *   - GATE DOS STEPPERS VEM DO BANCO: role_seats (quota_policies.*_seats).
 *     limit 0 = plano não inclui o papel → stepper NÃO renderiza.
 *     Ex.: Basic tem nutritionist_seats=0 → sem stepper de nutricionista.
 *     NENHUMA regra de papel hardcoded aqui.
 *   - READ-ONLY: nada aqui escreve, cria assinatura ou toca catálogo.
 *     CTA = "Falar com a equipe".
 *   - Paleta: preto/branco/cinza (UI_PADRAO — sem verde/âmbar/vermelho).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicPlans, formatBRL, computeTotal } from '../../services/publicPlansService';

const UI = {
  text: '#0b0b0f',
  muted: '#5b5b66',
  border: '#e7e7ee',
  card: '#ffffff',
  card2: '#fafafe',
  accent: '#0b0b0f',
};

const FAMILY_ORDER = ['basic', 'basic_plus', 'pro', 'elite'];
const FAMILY_LABEL = {
  basic: 'Basic',
  basic_plus: 'Basic+',
  pro: 'Pro',
  elite: 'Elite',
};
const FAMILY_TAGLINE = {
  pt: {
    basic: 'Treinador + Atleta',
    basic_plus: 'Basic + Preparação Física',
    pro: 'Basic+ + Nutrição',
    elite: 'Equipe completa + Gestão',
  },
  en: {
    basic: 'Coach + Athlete',
    basic_plus: 'Basic + Physical Training',
    pro: 'Basic+ + Nutrition',
    elite: 'Full team + Management',
  },
};
const SIZES = ['S', 'M', 'L', 'XL'];

// Textos por idioma (nomes comerciais Basic/Basic+/Pro/Elite NÃO se traduzem)
const T = {
  pt: {
    title: 'Planos',
    subtitle: 'Escolha os perfis que sua equipe precisa. Ajuste o tamanho conforme o clube.',
    sizeAria: 'Tamanho do plano',
    sizeHint: { S: 'Até 5 atletas (Elite: 10)', M: 'Até 25 atletas', L: 'Até 50 atletas', XL: 'Até 100 atletas' },
    perMonth: '/mês',
    baseExtras: 'base + extras',
    annual: (v) => `Anual: ${v} (2 meses grátis)`,
    upToAthletes: (n) => <>Até <strong>{n}</strong> atletas</>,
    aiCredits: (n) => <><strong>{n}</strong> interações de IA/mês</>,
    storage: (gb) => `${gb} GB de armazenamento`,
    customize: { open: '+ Personalizar equipe', close: '− Personalizar equipe' },
    athletePack: '+5 atletas',
    perPack: (v) => `${v}/mês por pack`,
    perUser: (v) => `${v}/mês por usuário`,
    extrasNote: 'Extras disponíveis conforme os perfis incluídos no plano.',
    cta: 'Falar com a equipe',
    highlight: 'COMPLETO',
    steppers: {
      coach_seats: 'Treinador extra',
      prep_seats: 'Preparador extra',
      nutritionist_seats: 'Nutricionista extra',
      manager_seats: 'Gestor extra',
    },
    stepAdd: 'Adicionar',
    stepRemove: 'Remover',
    enterprise: 'Enterprise',
    enterpriseDesc: 'Mais de 100 atletas, múltiplas unidades ou requisitos dedicados — proposta sob medida.',
  },
  en: {
    title: 'Plans',
    subtitle: 'Choose the roles your team needs. Adjust the size to fit your club.',
    sizeAria: 'Plan size',
    sizeHint: { S: 'Up to 5 athletes (Elite: 10)', M: 'Up to 25 athletes', L: 'Up to 50 athletes', XL: 'Up to 100 athletes' },
    perMonth: '/month',
    baseExtras: 'base + extras',
    annual: (v) => `Annual: ${v} (2 months free)`,
    upToAthletes: (n) => <>Up to <strong>{n}</strong> athletes</>,
    aiCredits: (n) => <><strong>{n}</strong> AI interactions/month</>,
    storage: (gb) => `${gb} GB of storage`,
    customize: { open: '+ Customize team', close: '− Customize team' },
    athletePack: '+5 athletes',
    perPack: (v) => `${v}/month per pack`,
    perUser: (v) => `${v}/month per user`,
    extrasNote: 'Extras available based on the roles included in the plan.',
    cta: 'Talk to our team',
    highlight: 'COMPLETE',
    steppers: {
      coach_seats: 'Extra coach',
      prep_seats: 'Extra physical trainer',
      nutritionist_seats: 'Extra nutritionist',
      manager_seats: 'Extra manager',
    },
    stepAdd: 'Add',
    stepRemove: 'Remove',
    enterprise: 'Enterprise',
    enterpriseDesc: 'More than 100 athletes, multiple units or dedicated requirements — custom proposal.',
  },
};

// Papéis compráveis como extra — a DISPONIBILIDADE vem de role_seats (banco).
const ROLE_STEPPER_KEYS = ['coach_seats', 'prep_seats', 'nutritionist_seats', 'manager_seats'];

function Stepper({ label, value, onChange, priceLabel, addLabel, removeLabel }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div>
        <div className="text-sm font-medium" style={{ color: UI.text }}>{label}</div>
        <div className="text-xs" style={{ color: UI.muted }}>{priceLabel}</div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          className="w-7 h-7 rounded-lg border text-sm font-semibold"
          style={{ borderColor: UI.border, color: UI.text }}
          aria-label={`${removeLabel} ${label}`}
        >−</button>
        <span className="w-6 text-center text-sm font-semibold tabular-nums" style={{ color: UI.text }}>
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          className="w-7 h-7 rounded-lg border text-sm font-semibold"
          style={{ borderColor: UI.border, color: UI.text }}
          aria-label={`${addLabel} ${label}`}
        >+</button>
      </div>
    </div>
  );
}

function PlanCard({ plan, highlight, t, lang }) {
  const [expanded, setExpanded] = useState(false);
  const [extras, setExtras] = useState({ athletePacks: 0, staffByRole: {} });

  if (!plan) return null;

  const staffTotal = Object.values(extras.staffByRole).reduce((a, b) => a + b, 0);
  const totalCents = computeTotal(plan, { athletePacks: extras.athletePacks, staff: staffTotal });
  const hasExtras = extras.athletePacks > 0 || staffTotal > 0;

  // GATE: steppers só para papéis que o plano inclui (role_seats > 0, do banco)
  const availableRoles = ROLE_STEPPER_KEYS.filter(
    (key) => (plan.role_seats?.[key] || 0) > 0
  );

  const athletePackPrice = 5 * (plan.price_per_athlete_cents || 0);
  const staffPrice = plan.price_per_staff_cents || 0;

  return (
    <div
      className="rounded-2xl border p-6 flex flex-col"
      style={{
        borderColor: highlight ? UI.text : UI.border,
        borderWidth: highlight ? 2 : 1,
        background: UI.card,
      }}
    >
      <div className="flex items-baseline justify-between">
        <div className="text-lg font-semibold" style={{ color: UI.text }}>
          {FAMILY_LABEL[plan.plan_family] || plan.plan_family}
        </div>
        {highlight && (
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full border"
            style={{ borderColor: UI.text, color: UI.text }}
          >
            {t.highlight}
          </span>
        )}
      </div>
      <div className="mt-1 text-xs" style={{ color: UI.muted }}>
        {FAMILY_TAGLINE[lang][plan.plan_family]}
      </div>

      <div className="mt-5">
        <span className="text-3xl font-semibold tabular-nums" style={{ color: UI.text }}>
          {formatBRL(hasExtras ? totalCents : plan.price_brl_cents)}
        </span>
        <span className="text-sm" style={{ color: UI.muted }}>{t.perMonth}</span>
        {hasExtras && (
          <div className="text-xs mt-1" style={{ color: UI.muted }}>
            {formatBRL(plan.price_brl_cents)} {t.baseExtras}
          </div>
        )}
      </div>
      {plan.annual_price_cents ? (
        <div className="mt-1 text-xs" style={{ color: UI.muted }}>
          {t.annual(formatBRL(plan.annual_price_cents))}
        </div>
      ) : null}

      <ul className="mt-5 space-y-2 text-sm" style={{ color: UI.text }}>
        {plan.athlete_limit != null && <li>{t.upToAthletes(plan.athlete_limit)}</li>}
        {plan.ai_credits_monthly != null && (
          <li>{t.aiCredits(Number(plan.ai_credits_monthly).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR'))}</li>
        )}
        {plan.storage_mb != null && <li>{t.storage(Math.round(plan.storage_mb / 1024))}</li>}
      </ul>

      {/* Calculadora de extras — segundo passo, opcional */}
      <div className="mt-5 pt-4 border-t" style={{ borderColor: UI.border }}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm font-medium hover:opacity-80 transition-opacity"
          style={{ color: UI.text }}
        >
          {expanded ? t.customize.close : t.customize.open}
        </button>

        {expanded && (
          <div className="mt-2 divide-y" style={{ borderColor: UI.border }}>
            <Stepper
              label={t.athletePack}
              priceLabel={t.perPack(formatBRL(athletePackPrice))}
              value={extras.athletePacks}
              onChange={(v) => setExtras((p) => ({ ...p, athletePacks: v }))}
              addLabel={t.stepAdd}
              removeLabel={t.stepRemove}
            />
            {availableRoles.map((key) => (
              <Stepper
                key={key}
                label={t.steppers[key]}
                priceLabel={t.perUser(formatBRL(staffPrice))}
                value={extras.staffByRole[key] || 0}
                onChange={(v) =>
                  setExtras((p) => ({
                    ...p,
                    staffByRole: { ...p.staffByRole, [key]: v },
                  }))
                }
                addLabel={t.stepAdd}
                removeLabel={t.stepRemove}
              />
            ))}
            <p className="pt-2 text-[11px]" style={{ color: UI.muted }}>
              {t.extrasNote}
            </p>
          </div>
        )}
      </div>

      <div className="mt-auto pt-5">
        <Link
          to="/contato"
          className="block w-full text-center py-2.5 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
          style={{ background: UI.accent, color: '#fff' }}
        >
          {t.cta}
        </Link>
      </div>
    </div>
  );
}

export default function PricingSection({ lang = 'pt', showError = false }) {
  const t = T[lang] || T.pt;
  const [plans, setPlans] = useState(null);
  const [size, setSize] = useState('S');
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getPublicPlans();
        if (mounted) setPlans(p);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[PricingSection] catálogo indisponível:', e?.message || e);
        }
        if (mounted) setError(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const cells = useMemo(() => {
    if (!plans) return {};
    const bySelected = {};
    for (const fam of FAMILY_ORDER) {
      bySelected[fam] = plans.find(
        (p) => p.plan_family === fam && p.size_band === size
      );
    }
    return bySelected;
  }, [plans, size]);

  if (error) {
    if (!showError) return null; // vitrine embutida: falha não quebra a landing
    return (
      <section className="max-w-3xl mx-auto px-6 py-16 text-center" id="planos">
        <h2 className="text-3xl font-semibold" style={{ color: UI.text }}>{t.title}</h2>
        <p className="mt-4 text-base" style={{ color: UI.muted }}>
          {lang === 'en'
            ? 'We could not load the plans right now. Please contact the VUKZ team.'
            : 'Não foi possível carregar os planos neste momento. Entre em contato com a equipe VUKZ.'}
        </p>
        <Link
          to="/pre-cadastro"
          className="inline-block mt-6 px-6 py-2.5 rounded-lg font-semibold text-sm text-white transition-opacity hover:opacity-90"
          style={{ background: UI.accent }}
        >
          {t.cta}
        </Link>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-6 py-16" id="planos">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-semibold" style={{ color: UI.text }}>{t.title}</h2>
        <p className="mt-2 text-base" style={{ color: UI.muted }}>
          {t.subtitle}
        </p>

        {/* Toggle S/M/L/XL */}
        <div
          className="inline-flex mt-6 rounded-xl border p-1 gap-1"
          style={{ borderColor: UI.border, background: UI.card2 }}
          role="tablist"
          aria-label={t.sizeAria}
        >
          {SIZES.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={size === s}
              onClick={() => setSize(s)}
              className="px-5 py-1.5 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: size === s ? UI.accent : 'transparent',
                color: size === s ? '#fff' : UI.muted,
              }}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="mt-2 text-xs" style={{ color: UI.muted }}>
          {t.sizeHint[size]}
        </div>
      </div>

      {!plans ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-96 rounded-2xl border animate-pulse"
                 style={{ borderColor: UI.border, background: UI.card2 }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FAMILY_ORDER.map((fam) => (
            <PlanCard key={fam} plan={cells[fam]} highlight={fam === 'elite'} t={t} lang={lang} />
          ))}
        </div>
      )}

      {/* Enterprise — sales-led, sem número */}
      <div
        className="mt-8 rounded-2xl border p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
        style={{ borderColor: UI.border, background: UI.card2 }}
      >
        <div>
          <div className="font-semibold" style={{ color: UI.text }}>{t.enterprise}</div>
          <div className="text-sm mt-1" style={{ color: UI.muted }}>
            {t.enterpriseDesc}
          </div>
        </div>
        <Link
          to="/contato"
          className="px-6 py-2.5 rounded-lg font-semibold text-sm border transition-opacity hover:opacity-80 whitespace-nowrap"
          style={{ borderColor: UI.text, color: UI.text }}
        >
          {t.cta}
        </Link>
      </div>
    </section>
  );
}
